import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Share2, Copy, RotateCcw, ExternalLink, Check, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { apiPut, apiPost } from '../../../api/client';
import type { Event, EventScaletta, ScalettaStep } from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { formatDate } from '../../../utils/format';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600,
  color: 'var(--color-text-muted)', marginBottom: '0.25rem',
};

function Field({ label, value, onChange, placeholder, type = 'text', hint }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; hint?: string;
}) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
      {hint && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.2rem' }}>{hint}</p>}
    </div>
  );
}

function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        <Clock size={13} style={{
          position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)',
          color: 'var(--color-text-muted)', pointerEvents: 'none',
        }} />
        <input
          type="time"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, paddingLeft: '2rem' }}
        />
      </div>
    </div>
  );
}

function SectionCard({ title, accent = false, children }: { title: string; accent?: boolean; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--glass-bg-card)', border: `1px solid ${accent ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
      borderRadius: 'var(--radius-card)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.625rem 1rem',
        background: accent ? 'oklch(from var(--color-primary) l c h / 0.06)' : 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: accent ? 'var(--color-primary)' : 'var(--color-text)' }}>
          {title}
        </p>
      </div>
      <div style={{ padding: '1rem' }}>
        {children}
      </div>
    </div>
  );
}

// ─── Form state ───────────────────────────────────────────────────────────────

const EMPTY_BASE = {
  subject1Name: '', subject1Phone: '', subject1Address: '', subject1Time: '',
  subject2Name: '', subject2Phone: '', subject2Address: '', subject2Time: '',
  churchName: '', churchAddress: '', ceremonyTime: '',
  notes: '',
};

type BaseForm = typeof EMPTY_BASE;

interface ScalettaForm extends BaseForm {
  steps: ScalettaStep[];
}

function genId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function newStep(label: string): ScalettaStep {
  return { id: genId(), label, time: null, name: null, address: null };
}

function getDefaultSteps(eventType: string): ScalettaStep[] {
  switch (eventType) {
    case 'MATRIMONIO':   return [newStep('Esterni'), newStep('Ricevimento / Ristorante')];
    case 'BATTESIMO':    return [newStep('Esterni'), newStep('Pranzo / Ristorante')];
    case 'COMUNIONE':
    case 'CRESIMA':      return [newStep('Esterni'), newStep('Pranzo / Cena')];
    case 'COMPLEANNO':   return [newStep('Esterni'), newStep('Festa / Ristorante')];
    case 'RITRATTO':     return [newStep('Esterni'), newStep('Location 3')];
    default:             return [newStep('Esterni'), newStep('Luogo 3')];
  }
}

function scalettaToForm(s: EventScaletta | null | undefined, eventType: string): ScalettaForm {
  const base: BaseForm = {
    subject1Name:    s?.subject1Name    ?? '',
    subject1Phone:   s?.subject1Phone   ?? '',
    subject1Address: s?.subject1Address ?? '',
    subject1Time:    s?.subject1Time    ?? '',
    subject2Name:    s?.subject2Name    ?? '',
    subject2Phone:   s?.subject2Phone   ?? '',
    subject2Address: s?.subject2Address ?? '',
    subject2Time:    s?.subject2Time    ?? '',
    churchName:      s?.churchName      ?? '',
    churchAddress:   s?.churchAddress   ?? '',
    ceremonyTime:    s?.ceremonyTime    ?? '',
    notes:           s?.notes           ?? '',
  };

  if (!s) return { ...base, steps: getDefaultSteps(eventType) };

  // Use extraSteps if already populated
  if (Array.isArray(s.extraSteps) && s.extraSteps.length > 0) {
    let steps = s.extraSteps.map(st => ({
      id:      st.id,
      label:   st.label,
      time:    st.time ?? null,
      name:    st.name ?? null,
      address: st.address ?? null,
    }));
    if (!steps.some(st => st.label === 'Esterni')) {
      steps = [newStep('Esterni'), ...steps];
    }
    return { ...base, steps };
  }

  // Migrate from legacy flat fields (backward compat)
  const migrated: ScalettaStep[] = [];
  if (s.preDinnerLocation || s.preDinnerTime || s.preDinnerAddress) {
    migrated.push({ id: genId(), label: 'Esterni', time: s.preDinnerTime ?? null, name: s.preDinnerLocation ?? null, address: s.preDinnerAddress ?? null });
  }
  if (s.restaurantName || s.restaurantTime || s.restaurantAddress) {
    migrated.push({ id: genId(), label: 'Ricevimento / Ristorante', time: s.restaurantTime ?? null, name: s.restaurantName ?? null, address: s.restaurantAddress ?? null });
  }

  return { ...base, steps: migrated.length > 0 ? migrated : getDefaultSteps(eventType) };
}

// Labels for fixed sections vary by event type
function getFixedLabels(eventType: string) {
  switch (eventType) {
    case 'MATRIMONIO':
      return { s1: 'Sposo', s2: 'Sposa', church: 'Cerimonia' };
    case 'BATTESIMO':
      return { s1: 'Famiglia / Neonato/a', s2: 'Ospiti', church: 'Cerimonia' };
    case 'COMUNIONE': case 'CRESIMA':
      return { s1: 'Festeggiato/a', s2: 'Famiglia', church: 'Cerimonia' };
    case 'COMPLEANNO':
      return { s1: 'Festeggiato/a', s2: 'Ospite speciale', church: 'Venue' };
    case 'RITRATTO':
      return { s1: 'Cliente', s2: 'Secondo soggetto', church: 'Location 1' };
    default:
      return { s1: 'Soggetto 1', s2: 'Soggetto 2', church: 'Luogo cerimonia' };
  }
}

// ─── Dynamic step card ────────────────────────────────────────────────────────

const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem',
  color: 'var(--color-text-faint)', borderRadius: 'var(--radius-interactive)',
  display: 'flex', alignItems: 'center',
};

function StepCard({ step, onChange, onRemove, onMove, canRemove, isFirst, isLast }: {
  step: ScalettaStep;
  onChange: (updated: ScalettaStep) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  canRemove: boolean;
  isFirst: boolean;
  isLast: boolean;
}) {
  const upd = (key: keyof ScalettaStep) => (v: string) =>
    onChange({ ...step, [key]: v || null });

  return (
    <div style={{
      background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)', overflow: 'hidden',
    }}>
      {/* Header: editable label + move/remove buttons */}
      <div style={{
        padding: '0.5rem 0.75rem 0.5rem 1rem',
        background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', gap: '0.25rem',
      }}>
        <input
          value={step.label}
          onChange={e => onChange({ ...step, label: e.target.value })}
          placeholder="Nome step…"
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            borderBottom: '1px dashed rgba(255,255,255,0.10)', cursor: 'text',
            fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-text)',
            paddingBottom: '0.1rem',
          }}
          onFocus={e => (e.currentTarget.style.borderBottomColor = 'var(--color-primary)')}
          onBlur={e => (e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.10)')}
        />
        <button
          onClick={() => onMove(-1)}
          disabled={isFirst}
          title="Sposta su"
          style={{ ...iconBtnStyle, opacity: isFirst ? 0.3 : 1, cursor: isFirst ? 'default' : 'pointer' }}
        >
          <ArrowUp size={14} />
        </button>
        <button
          onClick={() => onMove(1)}
          disabled={isLast}
          title="Sposta giù"
          style={{ ...iconBtnStyle, opacity: isLast ? 0.3 : 1, cursor: isLast ? 'default' : 'pointer' }}
        >
          <ArrowDown size={14} />
        </button>
        {canRemove && (
          <button
            onClick={onRemove}
            title="Rimuovi step"
            style={iconBtnStyle}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-danger, #e53e3e)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text-faint)')}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      {/* Fields */}
      <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '0.625rem' }}>
        <TimeField label="Orario" value={step.time ?? ''} onChange={upd('time')} />
        <Field label="Nome luogo" value={step.name ?? ''} onChange={upd('name')} placeholder="es. Giardini della Villa" />
        <Field label="Indirizzo" value={step.address ?? ''} onChange={upd('address')} placeholder="Via Verde 10, Monza" />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ScalettaTab({ event }: { event: Event }) {
  const qc = useQueryClient();
  const labels = getFixedLabels(event.eventType);

  const [form, setForm] = useState<ScalettaForm>(() => scalettaToForm(event.scaletta, event.eventType));
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(() => {
    const token = event.scaletta?.shareToken;
    if (!token) return null;
    return `${window.location.origin}/api/public/scaletta/${token}`;
  });
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Initialize form once from event (don't overwrite user edits on refetch)
  useEffect(() => {
    if (!loaded) {
      setForm(scalettaToForm(event.scaletta, event.eventType));
      setLoaded(true);
    }
  }, [event.scaletta, event.eventType, loaded]);

  const updBase = (key: keyof BaseForm) => (v: string) => setForm(f => ({ ...f, [key]: v }));

  // ── Steps helpers ────────────────────────────────────────────────────────────
  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, newStep('Nuovo step')] }));

  const removeStep = (id: string) =>
    setForm(f => ({ ...f, steps: f.steps.filter(s => s.id !== id) }));

  const updateStep = (updated: ScalettaStep) =>
    setForm(f => {
      const prev = f.steps.find(s => s.id === updated.id);
      const steps = f.steps.map(s => s.id === updated.id ? updated : s);
      if (prev?.time !== updated.time) {
        const withTime = steps.filter(s => s.time);
        const withoutTime = steps.filter(s => !s.time);
        withTime.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
        return { ...f, steps: [...withTime, ...withoutTime] };
      }
      return { ...f, steps };
    });

  const moveStep = (id: string, dir: -1 | 1) =>
    setForm(f => {
      const steps = [...f.steps];
      const i = steps.findIndex(s => s.id === id);
      const j = i + dir;
      if (j < 0 || j >= steps.length) return f;
      [steps[i], steps[j]] = [steps[j], steps[i]];
      return { ...f, steps };
    });

  // ── Autosave — debounced 1.5s ────────────────────────────────────────────────
  const saveScaletta = useCallback(async (data: ScalettaForm) => {
    setSaving(true);
    try {
      const { steps, ...flat } = data;
      const payload = {
        ...Object.fromEntries(
          Object.entries(flat).map(([k, v]) => [k, (v as string).trim() || null])
        ),
        extraSteps: (steps ?? []).map(s => ({
          id:      s.id,
          label:   s.label.trim() || 'Step',
          time:    s.time?.trim() || null,
          name:    s.name?.trim() || null,
          address: s.address?.trim() || null,
        })),
      };
      await apiPut(`/events/${event.id}/scaletta`, payload);
      qc.invalidateQueries({ queryKey: ['event', event.id] });
    } catch {
      toast.error('Errore nel salvataggio scaletta');
    }
    setSaving(false);
  }, [event.id, qc]);

  useEffect(() => {
    if (!loaded) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveScaletta(form), 1500);
    return () => clearTimeout(saveTimer.current);
  }, [form, loaded, saveScaletta]);

  // ── Share token mutation ─────────────────────────────────────────────────────
  const tokenMutation = useMutation({
    mutationFn: () => apiPost<{ token: string; shareUrl: string }>(`/events/${event.id}/scaletta/share-token`, {}),
    onSuccess: (res) => {
      setShareUrl(`${window.location.origin}/api/public/scaletta/${res.token}`);
      qc.invalidateQueries({ queryKey: ['event', event.id] });
      toast.success('Link generato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copiato!');
    });
  };

  const whatsappUrl = shareUrl
    ? `https://wa.me/?text=${encodeURIComponent(`Scaletta ${event.title} — ${formatDate(event.eventDate, 'd MMMM yyyy')}\n${shareUrl}`)}`
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── Share card ──────────────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)', padding: '1rem',
        display: 'flex', flexDirection: 'column', gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Share2 size={16} style={{ color: 'var(--color-text-muted)' }} />
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>Scaletta condivisibile</span>
          </div>
          {saving && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
              Salvataggio...
            </span>
          )}
        </div>

        {!shareUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Genera un link pubblico da inviare su WhatsApp — il cliente, il ristorante e i collaboratori possono consultarlo senza login.
            </p>
            <Button
              size="sm"
              icon={<Share2 size={14} />}
              onClick={() => tokenMutation.mutate()}
              loading={tokenMutation.isPending}
              style={{ alignSelf: 'flex-start' }}
            >
              Genera link condivisibile
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)',
              padding: '0.5rem 0.75rem', border: '1px solid rgba(255,255,255,0.10)',
              overflow: 'hidden',
            }}>
              <span style={{ flex: 1, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {shareUrl}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
              <Button size="sm" variant="secondary" icon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={copyLink}>
                {copied ? 'Copiato!' : 'Copia link'}
              </Button>
              <Button size="sm" variant="secondary" icon={<ExternalLink size={14} />}
                onClick={() => window.open(shareUrl, '_blank')}>
                Apri
              </Button>
              <Button size="sm" variant="ghost" icon={<RotateCcw size={14} />}
                onClick={() => { if (confirm('Rigenerare il link? Il vecchio non funzionerà più.')) tokenMutation.mutate(); }}
                loading={tokenMutation.isPending}>
                Rigenera
              </Button>
            </div>
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem',
                  background: '#25d366', color: '#fff', textDecoration: 'none',
                  padding: '0.625rem 1rem', borderRadius: 'var(--radius-card)',
                  fontSize: 'var(--text-sm)', fontWeight: 700,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                Invia su WhatsApp
              </a>
            )}
          </div>
        )}
      </div>

      {/* ── Subjects ────────────────────────────────────────────────────────── */}
      <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <SectionCard title={labels.s1}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <TimeField label="Orario" value={form.subject1Time} onChange={updBase('subject1Time')} />
            <Field label="Nome" value={form.subject1Name} onChange={updBase('subject1Name')} placeholder="es. Marco Bianchi" />
            <Field label="Telefono" value={form.subject1Phone} onChange={updBase('subject1Phone')} placeholder="es. +39 333 1234567" type="tel" />
            <Field label="Indirizzo / Location" value={form.subject1Address} onChange={updBase('subject1Address')} placeholder="Via Roma 1, Milano" />
          </div>
        </SectionCard>

        <SectionCard title={labels.s2}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <TimeField label="Orario" value={form.subject2Time} onChange={updBase('subject2Time')} />
            <Field label="Nome" value={form.subject2Name} onChange={updBase('subject2Name')} placeholder="es. Sara Verdi" />
            <Field label="Telefono" value={form.subject2Phone} onChange={updBase('subject2Phone')} placeholder="es. +39 333 7654321" type="tel" />
            <Field label="Indirizzo / Location" value={form.subject2Address} onChange={updBase('subject2Address')} placeholder="Via Garibaldi 5, Milano" />
          </div>
        </SectionCard>
      </div>

      {/* ── Cerimonia ───────────────────────────────────────────────────────── */}
      <SectionCard title={labels.church}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '0.625rem' }}>
          <TimeField label="Orario" value={form.ceremonyTime} onChange={updBase('ceremonyTime')} />
          <Field label="Nome luogo" value={form.churchName} onChange={updBase('churchName')} placeholder="es. Basilica di S. Maria" />
          <Field label="Indirizzo" value={form.churchAddress} onChange={updBase('churchAddress')} placeholder="Via Duomo 1, Milano" />
        </div>
      </SectionCard>

      {/* ── Dynamic steps ───────────────────────────────────────────────────── */}
      {(form.steps ?? []).map((step, index) => (
        <StepCard
          key={step.id}
          step={step}
          onChange={updateStep}
          onRemove={() => removeStep(step.id)}
          onMove={dir => moveStep(step.id, dir)}
          canRemove={form.steps.length > 1}
          isFirst={index === 0}
          isLast={index === (form.steps ?? []).length - 1}
        />
      ))}

      {/* ── Add step button ──────────────────────────────────────────────────── */}
      <button
        onClick={addStep}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          padding: '0.625rem 1rem',
          background: 'var(--glass-bg-card)', border: '1px dashed rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)', cursor: 'pointer',
          color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', fontWeight: 600,
          transition: 'border-color 0.15s, color 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = 'var(--color-primary)';
          e.currentTarget.style.color = 'var(--color-primary)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
          e.currentTarget.style.color = 'var(--color-text-muted)';
        }}
      >
        <Plus size={15} />
        Aggiungi step
      </button>

      {/* ── Note ────────────────────────────────────────────────────────────── */}
      <SectionCard title="Note per il giorno">
        <textarea
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Informazioni extra: dress code, riferimenti speciali, indicazioni al ristorante, contatti aggiuntivi…"
          style={{
            ...inputStyle,
            minHeight: 100, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6,
          }}
        />
      </SectionCard>

    </div>
  );
}
